import { lazy, Suspense } from 'react';
import { createBrowserRouter as _createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';
import * as Sentry from '@sentry/react';
const createBrowserRouter = Sentry.wrapCreateBrowserRouterV7(_createBrowserRouter);
import { AppShell } from '@/components/ui/AppShell';
import { InstallerShell } from '@/components/ui/InstallerShell';
import { CallbackView } from '@/components/auth/CallbackView';
import { TaskListView } from '@/views/TaskListView';
import { ProjectView } from '@/views/ProjectView';
import { PlanningView } from '@/views/PlanningView';
import { InstallerTaskListView } from '@/views/InstallerTaskListView';
import { InstallerProjectView } from '@/views/InstallerProjectView';
import { InstallerWerkView } from '@/views/InstallerWerkView';
import { InterventieShell } from '@/components/ui/InterventieShell';
import { InterventieTaskListView } from '@/views/InterventieTaskListView';
import { InterventieProjectView } from '@/views/InterventieProjectView';
import { AdminView } from '@/views/AdminView';
import { ControleView } from '@/views/ControleView';
import { OnderaannemersView } from '@/views/OnderaannemersView';
import { OnderaannemerPrijzenView } from '@/views/OnderaannemerPrijzenView';
import { EasykitPlanningView } from '@/views/EasykitPlanningView';
import { EasykitDriverView } from '@/views/EasykitDriverView';
import { EasykitDriverMirrorView } from '@/views/EasykitDriverMirrorView';
import { EasykitDriverTaskDetail } from '@/views/EasykitDriverTaskDetail';
import { EasykitActivityLogView } from '@/views/EasykitActivityLogView';
import { PlanningActivityLogView } from '@/views/PlanningActivityLogView';
import { AdminActivityLogView } from '@/views/AdminActivityLogView';
import { EasykitPhotoBrowserView } from '@/views/EasykitPhotoBrowserView';
import { PhotoBrowserView } from '@/views/PhotoBrowserView';
import { EasykitOplijsterView } from '@/views/EasykitOplijsterView';
import { EasykitInkooporderView } from '@/views/EasykitInkooporderView';
import { MateriaalProjectenView } from '@/views/MateriaalProjectenView';
import { FotoReviewView } from '@/views/FotoReviewView';
import { FotoReviewProjectView } from '@/views/FotoReviewProjectView';
import { WarehouseWervenView } from '@/views/WarehouseWervenView';
import { WarehouseWerfDetailView } from '@/views/WarehouseWerfDetailView';
import { OpmetingView } from '@/views/OpmetingView';
import { AdministratieView } from '@/views/AdministratieView';
import { usePermissionsStore } from '@/stores/permissions';

const BestandenHome = lazy(() =>
  import('@/views/BestandenHome').then((m) => ({ default: m.BestandenHome })),
);
const BestandenProjectenPicker = lazy(() =>
  import('@/views/BestandenProjectenPicker').then((m) => ({ default: m.BestandenProjectenPicker })),
);
const BestandenProjectVerkenner = lazy(() =>
  import('@/views/BestandenProjectVerkenner').then((m) => ({ default: m.BestandenProjectVerkenner })),
);
// Lazy-loaded — recharts (~heavy) only ships when /admin/uitgevoerde-omzet is hit.
const UitgevoerdeOmzetView = lazy(() =>
  import('@/views/UitgevoerdeOmzetView').then((m) => ({ default: m.UitgevoerdeOmzetView })),
);
// Lazy-loaded — recharts (TimeBucketChart) only ships when /voorraad is hit.
const VoorraadView = lazy(() =>
  import('@/views/VoorraadView').then((m) => ({ default: m.VoorraadView })),
);

// Lazy-loaded — the Van Marcke rekenblad bundles its engine and price-list JSON
// (ADR-105), which only ships when /vanmarcke is hit.
const VanMarckeView = lazy(() =>
  import('@/views/VanMarckeView').then((m) => ({ default: m.VanMarckeView })),
);

const GroepsdataView = lazy(() =>
  import('@/views/GroepsdataView').then((m) => ({ default: m.GroepsdataView })),
);

// Bright Analytics' key page and Igor's admin side (docs/work-items/bright-api-keys.md).
const BrightApiView = lazy(() =>
  import('@/views/BrightApiView').then((m) => ({ default: m.BrightApiView })),
);
const AdminBrightView = lazy(() =>
  import('@/views/AdminBrightView').then((m) => ({ default: m.AdminBrightView })),
);

function LazyFallback() {
  return (
    <div className="px-4 lg:px-8 py-12 text-center">
      <div
        className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
        role="status"
      >
        <span className="sr-only">Laden...</span>
      </div>
    </div>
  );
}

// easykit_planning precedes planning so projectleider_easykit (who has both) lands on EK.
// Regular projectleider lacks easykit_planning so still falls through to planning. Admin
// hits tasks first, unchanged.
// administratie sits BEFORE planning: the administratie role has both, and the
// dashboard is its daily tool. Admin matches `tasks` first, so this is safe.
// warehouse_werven sits BEFORE planning: warehouse now also has the `planning`
// view (read-only overview), but their home is the pick-list. Only warehouse +
// admin have warehouse_werven, and admin matches `tasks` first, so this is safe.
// bright_api is last: only the bright-api role has it, and its only page is /bright.
const VIEW_ROUTE_ORDER = ['tasks', 'easykit_planning', 'warehouse_werven', 'administratie', 'planning', 'easykit_oplijster', 'easykit_inkooporder', 'materiaal_projects', 'project', 'installer', 'interventie', 'easykit_driver', 'opmeting', 'admin', 'bestanden', 'bright_api'] as const;

function RoleRedirect() {
  const canView = usePermissionsStore((s) => s.canView);
  const loading = usePermissionsStore((s) => s.loading);
  const permissions = usePermissionsStore((s) => s.permissions);

  if (loading || !permissions) return null;

  for (const view of VIEW_ROUTE_ORDER) {
    if (canView(view)) {
      if (view === 'installer') return <Navigate to="/installer" replace />;
      if (view === 'interventie') return <Navigate to="/interventie" replace />;
      if (view === 'project') return <Navigate to="/tasks" replace />;
      if (view === 'easykit_planning') return <Navigate to="/easykit" replace />;
      if (view === 'easykit_oplijster') return <Navigate to="/easykit-oplijster" replace />;
      if (view === 'easykit_inkooporder') return <Navigate to="/easykit-inkooporder" replace />;
      if (view === 'materiaal_projects') return <Navigate to="/materiaal-projecten" replace />;
      if (view === 'warehouse_werven') return <Navigate to="/werven" replace />;
      if (view === 'easykit_driver') return <Navigate to="/easykit/driver" replace />;
      if (view === 'opmeting') return <Navigate to="/opmeting" replace />;
      if (view === 'bright_api') return <Navigate to="/bright" replace />;
      return <Navigate to={`/${view}`} replace />;
    }
  }

  return <Navigate to="/tasks" replace />;
}

function PermissionGuard({ view }: { view: string }) {
  const canView = usePermissionsStore((s) => s.canView);
  const loading = usePermissionsStore((s) => s.loading);
  const permissions = usePermissionsStore((s) => s.permissions);

  if (loading || !permissions) return null;

  if (!canView(view)) return <Navigate to="/" replace />;
  return <Outlet />;
}

function CollectorsView() {
  return <div>Collectors</div>;
}

function NotFoundView() {
  return <div>404 - Pagina niet gevonden</div>;
}

const base = import.meta.env.BASE_URL.replace(/\/$/, '');

export const routes: RouteObject[] = [
  {
    element: <AppShell />,
    children: [
      { index: true, element: <RoleRedirect /> },
      {
        element: <PermissionGuard view="tasks" />,
        children: [
          { path: 'tasks', element: <TaskListView /> },
        ],
      },
      {
        element: <PermissionGuard view="project" />,
        children: [
          { path: 'project/:projectId', element: <ProjectView /> },
        ],
      },
      {
        element: <PermissionGuard view="administratie" />,
        children: [
          { path: 'administratie', element: <AdministratieView /> },
        ],
      },
      {
        element: <PermissionGuard view="planning" />,
        children: [
          { path: 'planning', element: <PlanningView /> },
          { path: 'planning/log', element: <PlanningActivityLogView /> },
        ],
      },
      {
        element: <PermissionGuard view="tasks" />,
        children: [
          { path: 'collectors', element: <CollectorsView /> },
        ],
      },
      {
        element: <PermissionGuard view="photos_browser" />,
        children: [
          { path: 'photos', element: <PhotoBrowserView /> },
        ],
      },
      {
        element: <PermissionGuard view="foto_review" />,
        children: [
          { path: 'foto-controle', element: <FotoReviewView /> },
          { path: 'foto-controle/:projectId', element: <FotoReviewProjectView /> },
        ],
      },
      {
        element: <PermissionGuard view="easykit_planning" />,
        children: [
          { path: 'easykit', element: <EasykitPlanningView /> },
          { path: 'easykit/driver/:driverId', element: <EasykitDriverMirrorView /> },
          { path: 'easykit/photos', element: <EasykitPhotoBrowserView /> },
          { path: 'easykit/log', element: <EasykitActivityLogView /> },
        ],
      },
      {
        element: <PermissionGuard view="easykit_oplijster" />,
        children: [
          { path: 'easykit-oplijster', element: <EasykitOplijsterView /> },
        ],
      },
      {
        element: <PermissionGuard view="easykit_inkooporder" />,
        children: [
          { path: 'easykit-inkooporder', element: <EasykitInkooporderView /> },
        ],
      },
      {
        element: <PermissionGuard view="materiaal_projects" />,
        children: [
          { path: 'materiaal-projecten', element: <MateriaalProjectenView /> },
        ],
      },
      {
        element: <PermissionGuard view="warehouse_werven" />,
        children: [
          { path: 'werven', element: <WarehouseWervenView /> },
          { path: 'werven/:projectId', element: <WarehouseWerfDetailView /> },
        ],
      },
      {
        element: <PermissionGuard view="bestanden" />,
        children: [
          {
            path: 'bestanden',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <BestandenHome />
              </Suspense>
            ),
          },
          {
            path: 'bestanden/projecten',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <BestandenProjectenPicker />
              </Suspense>
            ),
          },
          {
            path: 'bestanden/projecten/:projectId',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <BestandenProjectVerkenner />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: <PermissionGuard view="opmeting" />,
        children: [
          {
            path: 'opmeting',
            element: <OpmetingView />,
          },
        ],
      },
      {
        element: <PermissionGuard view="stock" />,
        children: [
          {
            path: 'voorraad',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <VoorraadView />
              </Suspense>
            ),
          },
        ],
      },
      {
        // Deliberately absent from VIEW_ROUTE_ORDER: a tool, never a landing page.
        element: <PermissionGuard view="vanmarcke" />,
        children: [
          {
            path: 'vanmarcke/*',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <VanMarckeView />
              </Suspense>
            ),
          },
        ],
      },
      {
        // Admin only (docs/work-items/groepsdata-overzicht.md); not a landing page.
        element: <PermissionGuard view="groepsdata" />,
        children: [
          {
            path: 'groepsdata',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <GroepsdataView />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: <PermissionGuard view="bright_api" />,
        children: [
          {
            path: 'bright',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <BrightApiView />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: <PermissionGuard view="admin" />,
        children: [
          { path: 'admin', element: <AdminView /> },
          {
            path: 'admin/bright',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <AdminBrightView />
              </Suspense>
            ),
          },
          { path: 'admin/controle', element: <ControleView /> },
          { path: 'admin/log', element: <AdminActivityLogView /> },
          { path: 'admin/onderaannemers', element: <OnderaannemersView /> },
          { path: 'admin/oa-prijzen', element: <OnderaannemerPrijzenView /> },
          {
            path: 'admin/uitgevoerde-omzet',
            element: (
              <Suspense fallback={<LazyFallback />}>
                <UitgevoerdeOmzetView />
              </Suspense>
            ),
          },
        ],
      },
      // Task detail accessible by both easykit_planning and easykit_driver (API protects data)
      { path: 'easykit/driver/task/:taskId', element: <EasykitDriverTaskDetail /> },
    ],
  },
  {
    element: <InstallerShell />,
    children: [
      {
        element: <PermissionGuard view="installer" />,
        children: [
          { path: 'installer', element: <InstallerTaskListView /> },
          { path: 'installer/project/:projectId', element: <InstallerProjectView /> },
          // TODO Phase 3: guard with dedicated installer_werk view perm (for isOa gate)
          { path: 'installer/werk', element: <InstallerWerkView /> },
          // Limosa moved onto the job card; an old link or home-screen shortcut lands on the planning.
          { path: 'installer/limosa', element: <Navigate to="/installer" replace /> },
        ],
      },
      {
        element: <PermissionGuard view="easykit_driver" />,
        children: [
          { path: 'easykit/driver', element: <EasykitDriverView /> },
          { path: 'easykit/driver/task/:taskId', element: <EasykitDriverTaskDetail /> },
        ],
      },
    ],
  },
  {
    element: <InterventieShell />,
    children: [
      {
        element: <PermissionGuard view="interventie" />,
        children: [
          { path: 'interventie', element: <InterventieTaskListView /> },
          { path: 'interventie/project/:projectId', element: <InterventieProjectView /> },
        ],
      },
    ],
  },
  // OIDC callback — outside shells, no nav chrome needed
  { path: 'callback', element: <CallbackView /> },
  { path: '*', element: <NotFoundView /> },
];

export const router = createBrowserRouter(routes, { basename: base || undefined });
